import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_ROUTES from "../../api-routes";
import apiService from "../../services/service";
import { z } from "zod";
import useFileUpload from "~/utils/useFileUpload";
import { useSelector } from "react-redux";
import { format } from "date-fns";

let addressObj = {
  type: "Registered",
  country: "",
  address_line_1: "",
  address_line_2: "",
  state: "",
  city: "",
  postalcode: "",
};
const useShareholdersForm = ({
  setShowDialog,
  regData,
  setRegData,
  resetRegData,
  isNewShareholder,
}) => {
  const [showShareholdersRegistration, setShowShareholdersRegistration] =
    useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [btnToggle, setBtnToggle] = useState(true);
  const [formDetails, setFormDetails] = useState({});
  const [isCongrats, setIsCongrats] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entity, setEntity] = useState({});
  const [files, setFiles] = useState([]);
  const [shareholderType, setShareholderType] = useState("");
  const [addressData, setAddressData] = useState(addressObj);
  const [shareRegistryData, setShareRegistryData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [transaction, setTransaction] = useState({});
  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { id: userId } = useSelector((state) => state.login);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveIndex(0);
  }, []);

  const handleBack = () => {
    setActiveIndex((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleShareholderClick = () => {
    setShowShareholdersRegistration(true);
  };

  const handleShareholderSubmit = async () => {
    setIsLoading(true);
    let tags, metadata;
    const attachments = [];
    if (regData) {
      const shareholderData = {
        type: shareholderType,
        name: regData?.name || "",
        last_name: regData?.last_name || "",
        other_name: regData?.other_name || "",
        preferred_name: regData?.preferred_name || "",
        legal_name: regData?.legal_name || "",
        email: regData?.email || null,
        phone: regData?.phone || "",
        phone_code: regData?.phone_code || "",
        point_of_contact: regData?.point_of_contact || "",
        status: regData?.status.code || null,
        nationality: regData?.nationality.code || "",
        date_of_birth: regData?.date_of_birth || null,
        contact_email: regData?.contact_email || null,
        tax_identification_no: regData?.tax_identification_no || "",
        local_tax_id: regData?.local_tax_id || "",
        company_registration_no: regData?.company_registration_no || "",
        legal_structure: regData?.legal_structure.code || null,
        incorporation_country: regData?.incorporation_country.code || null,
      };

      const shareholderAddressData = {
        type: addressData.type,
        country:
          typeof addressData.country === "object"
            ? addressData.country.code
            : addressData.country || null,
        address_line_1: addressData.address_line_1 || "",
        address_line_2: addressData.address_line_2 || "",
        state: addressData.state?.code || "",
        city: addressData.city?.code || "",
        postalcode: addressData.postalcode || "",
      };

      const apiRoute = API_ROUTES.ADD_SHARE_HOLDER;
      try {
        const formData = new FormData();
        const fileOutput = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          formData.append("file", file);
          let fileData = {
            fileName: file?.fileName,
            category: file?.category,
            subCategory: file?.subcategory,
          };
          if (file?.description) {
            fileData.description = file?.description;
          }
          fileOutput.push(fileData);
        }

        formData.append("shareholder", JSON.stringify(shareholderData));
        formData.append(
          "shareholder_address",
          JSON.stringify(shareholderAddressData),
        );
        formData.append("share_registry", JSON.stringify(shareRegistryData));
        formData.append("filesData", JSON.stringify(fileOutput));
        tags = {
          entity: uuidToUlid(id),
          country: entity?.country,
          temporary_file: "true",
          year: new Date().getFullYear().toString(),
        };

        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          const uploadData = await uploadFile(file);
          attachments.push({ url: uploadData.url, fileName: uploadData.id });
        }

        const res = await apiService(apiRoute, "POST", {
          body: {
            shareholder: shareholderData,
            shareholder_address: shareholderAddressData,
            share_registry: shareRegistryData,
            attachments,
          },
          replace: { id },
          schema: z.object({
            id: z.string(),
          }),
        });

        tags = { ...tags, shareHolder: uuidToUlid(res.data.shareHolder) };
        for (let i = 0; i < files.length; i++) {
          const file = files[i]?.file;
          tags = {
            ...tags,
            category: file?.category,
            subCategory: file?.subcategory,
            temporary_file: "false",
          };
          const url = attachments?.find(
            (fileAttachment) => fileAttachment.fileName === file.fileName,
          )?.url;
          const version =
            file?.versionNumber.toString() || `${await getFileVersion(tags)}`;
          metadata = {
            original_file_name: encodeURIComponent(file.name),
            file_size: file.size.toString(),
            file_type: file.type,
            user: userId,
            file_name: file.fileName,
            version,
            created_at: new Date().toISOString(),
            file_object_type: "SHAREHOLDER",
            url,
            ...(file?.description && {
              description: encodeURIComponent(file?.description),
            }),
          };
          await updateTag(file.fileName, tags, metadata);
        }
        if (res) {
          resetRegData();
          setShareholderType("");
          setAddressData(addressObj);
          setShareRegistryData([]);
          setFiles([]);
          setShowDialog(false);
          setIsCongrats(true);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchEntity = async () => {
    const { data } = await apiService(API_ROUTES.FETCH_ENTITY, "GET", {
      replace: { id },
    });
    setEntity(data);
  };

  useEffect(() => {
    if (isNewShareholder) fetchEntity();
  }, []);

  useEffect(() => {
    if (typeof resetRegData === "function") {
      resetRegData();
    }
  }, [navigate]);

  const checkValue = (data) =>
    Object.values(data).some((val) =>
      val && typeof val === "object"
        ? checkValue(val)
        : val !== "" && val !== false && val !== null,
    );

  const handleDialogToggle = () => {
    if (regData) {
      const hasValue = checkValue(regData);
      setShowDialog(hasValue);
    }
  };

  useEffect(() => {
    handleDialogToggle();
  }, [regData]);

  const formatDate = (dateString) => {
    if (dateString) {
      return format(dateString, "MMMM do, yyyy");
    } else {
      ("-");
    }
  };

  return {
    handleShareholderClick,
    showShareholdersRegistration,
    handleBack,
    activeIndex,
    setActiveIndex,
    entityId: id,
    btnToggle,
    setBtnToggle,
    formDetails,
    setFormDetails,
    isCongrats,
    setIsCongrats,
    isLoading,
    setIsLoading,
    entity,
    setEntity,
    handleShareholderSubmit,
    files,
    setFiles,
    shareholderType,
    setShareholderType,
    addressData,
    setAddressData,
    shareRegistryData,
    setShareRegistryData,
    modalVisible,
    setModalVisible,
    transaction,
    setTransaction,
    formatDate,
  };
};

export default useShareholdersForm;
